import { getSessionHeaders } from '@/lib/cloudflare/auth-client';
import type { BillingInterval, PlanType } from '@/types/subscription';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';

interface BillingPathOptions {
  autoStartTrial?: boolean;
  billingRequired?: boolean;
  success?: boolean;
  canceled?: boolean;
  plan?: PlanType;
  interval?: BillingInterval;
}

interface GettingStartedPathOptions {
  success?: boolean;
  canceled?: boolean;
  plan?: PlanType;
  interval?: BillingInterval;
}

interface StartTrialCheckoutOptions {
  plan?: PlanType;
  interval?: BillingInterval;
  successPath?: string;
  cancelPath?: string;
}

export function hasBillingAccess(status: string | null | undefined): boolean {
  return status === 'active' || status === 'trialing';
}

export function buildBillingPath(options: BillingPathOptions = {}): string {
  const params = new URLSearchParams();

  if (options.autoStartTrial) params.set('autoStartTrial', 'true');
  if (options.billingRequired) params.set('billingRequired', 'true');
  if (options.success) params.set('success', 'true');
  if (options.canceled) params.set('canceled', 'true');
  if (options.plan) params.set('plan', options.plan);
  if (options.interval) params.set('interval', options.interval);

  const search = params.toString();
  return search ? `/billing?${search}` : '/billing';
}

export function buildGettingStartedPath(options: GettingStartedPathOptions = {}): string {
  const params = new URLSearchParams();

  if (options.success) params.set('success', 'true');
  if (options.canceled) params.set('canceled', 'true');
  if (options.plan) params.set('plan', options.plan);
  if (options.interval) params.set('interval', options.interval);

  const search = params.toString();
  return search ? `/getting-started?${search}` : '/getting-started';
}

export async function startTrialCheckout({
  plan = 'pro',
  interval = 'monthly',
  successPath,
  cancelPath,
}: StartTrialCheckoutOptions = {}): Promise<string> {
  const response = await fetch(`${CF_WORKER_URL}/api/stripe/checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getSessionHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ plan, interval, successPath, cancelPath }),
  });

  const data = await response.json();

  if (!response.ok || data?.error) {
    throw new Error(
      typeof data?.error === 'string'
        ? data.error
        : data?.error?.message || 'Failed to start checkout'
    );
  }

  const checkoutUrl = data?.data?.url || data?.url;
  if (!checkoutUrl) {
    throw new Error('Checkout session did not return a URL');
  }

  const parsedUrl = new URL(checkoutUrl);
  if (!parsedUrl.hostname.endsWith('.stripe.com')) {
    throw new Error('Invalid checkout URL');
  }

  return checkoutUrl;
}
